/* Admin Center — Firm Holidays tab (extracted from AdminPage.tsx) */

import { useState, useEffect } from 'react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { Trash2 } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import { VacationService } from '../../services/vacationService';
import type { FirmHoliday } from '../../../shared/types';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import { inputStyle, btnPrimary, btnDanger, RowSkeleton } from './shared';

/* ─── Firm Holidays Tab ─── */
function FirmHolidaysTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<FirmHoliday[]>([]);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newHours, setNewHours] = useState('8');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingDate, setEditingDate] = useState('');
  const [editingHours, setEditingHours] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['firm_holidays'], () => load());
  async function load() {
    try {
      const list = await VacationService.holidays.list();
      setItems(list.sort((a, b) => a.date.localeCompare(b.date)));
    } finally {
      setLoading(false);
    }
  }

  async function add() {
    if (!newName.trim() || !newDate) return;
    try {
      await VacationService.holidays.save({
        id: '',
        name: newName.trim(),
        date: newDate,
        hours: parseFloat(newHours) || 8,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      setNewDate('');
      setNewHours('8');
      await load();
      toast('Holiday added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim() || !editingDate) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await VacationService.holidays.save({
          ...item,
          name: editingName.trim(),
          date: editingDate,
          hours: parseFloat(editingHours) || 8,
        });
        setEditingId(null);
        await load();
        toast('Holiday updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this holiday?')) return;
    try {
      await VacationService.holidays.delete(id);
      await load();
      toast('Holiday deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        Firm-wide holidays that apply to all team members automatically. These will appear on
        timesheets and reduce forecasting capacity.
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(3),
            }}
          >
            Holiday Name
          </div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="e.g. Christmas Day..."
            style={inputStyle}
          />
        </div>
        <div style={{ width: rem(160) }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(3),
            }}
          >
            Date
          </div>
          <input
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            type="date"
            style={inputStyle}
          />
        </div>
        <div style={{ width: rem(100) }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(3),
            }}
          >
            Hours
          </div>
          <input
            value={newHours}
            onChange={(e) => setNewHours(e.target.value)}
            type="number"
            step="0.5"
            min="0"
            max="24"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <button onClick={add} style={{ ...btnPrimary, marginBottom: 0 }}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: rem(8),
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading firm holidays" />
        ) : items.length === 0 ? (
          <EmptyState title="No firm holidays defined yet" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                padding: `${rem(6)} var(--space-3)`,
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sa)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  flex: 1,
                }}
              >
                Holiday
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: rem(120),
                  textAlign: 'center',
                }}
              >
                Date
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: rem(70),
                  textAlign: 'right',
                }}
              >
                Hours
              </span>
              <span style={{ width: rem(100) }}></span>
            </div>
            {items.map((item, i) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                }}
              >
                {editingId === item.id ? (
                  <>
                    <input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                      style={{ ...inputStyle, flex: 1 }}
                      autoFocus
                    />
                    <input
                      value={editingDate}
                      onChange={(e) => setEditingDate(e.target.value)}
                      type="date"
                      style={{ ...inputStyle, width: rem(120) }}
                    />
                    <input
                      value={editingHours}
                      onChange={(e) => setEditingHours(e.target.value)}
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      style={{
                        ...inputStyle,
                        width: rem(70),
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    />
                    <button
                      onClick={() => update(item.id)}
                      style={{ ...btnPrimary, padding: `${rem(5)} ${rem(10)}` }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-base)' }}>{item.name}</span>
                    <span
                      style={{
                        width: rem(120),
                        textAlign: 'center',
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      }}
                    >
                      {item.date}
                    </span>
                    <span
                      style={{
                        width: rem(70),
                        textAlign: 'right',
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {item.hours}h
                    </span>
                    <button
                      onClick={() => {
                        setEditingId(item.id);
                        setEditingName(item.name);
                        setEditingDate(item.date);
                        setEditingHours(String(item.hours));
                      }}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(item.id)}
                      style={btnDanger}
                      title="Delete holiday"
                      aria-label={`Delete holiday ${item.name}`}
                    >
                      <Icon icon={Trash2} em={14 / 12} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { FirmHolidaysTab };
